import { useState } from 'react'
import type { DraftState, SheetMetadataField, SheetState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { ColumnTarget, SheetRole } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { columnTargets, METADATA_FIELD_OPTIONS, retarget, SHEET_ROLES } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { controlSheetRows, isArchiveOrder, sheetRowCounts } from '../../../Services/OrderWizard/archive.ts'
import { describeUnreadableCoordinates } from '../../../Services/OrderWizard/sheet/sheetImport.ts'
import { coordinateSwapHint, describeCoordinateSwap, swappedCoordinateTargets } from '../../../Services/OrderWizard/sheet/coordinateSwap.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { SHEET_READD_TARGET } from '../../../Services/OrderWizard/issues.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { Callout, SectionHeading } from './Primitives.tsx'

const ROLE_LABELS: Record<SheetRole, string> = {
  sampleName: 'Customer sample name',
  sampleId: 'Sample id',
  fileForward: 'File (forward)',
  fileReverse: 'File (reverse)',
  forwardPrimer: 'Forward primer',
  reversePrimer: 'Reverse primer',
  forwardTag: 'Forward tag',
  reverseTag: 'Reverse tag',
  itemType: 'Sample type',
  pool: 'Pool'
}

/* in the order the chips and the editor list them */
const ROLE_ORDER = Object.keys(ROLE_LABELS) as SheetRole[]

const FIELD_LABELS: Record<SheetMetadataField, string> = {
  coordinatesMerged: 'Coordinates (lat,lon)',
  coordinatesLat: 'Latitude',
  coordinatesLon: 'Longitude',
  samplingDate: 'Sampling date',
  samplingStart: 'Sampling start',
  samplingEnd: 'Sampling end',
  matrixType: 'Matrix type',
  extractionKit: 'Extraction kit',
  trapType: 'Trap type',
  collectionType: 'Collection type',
  sequencer: 'Sequencer',
  sequencingChemistry: 'Sequencing chemistry',
  sequencingDepth: 'Sequencing depth',
  localityType: 'Locality type',
  habitatType: 'Habitat type'
}

const targetLabel = (target: ColumnTarget): string =>
  target === 'custom'
    ? 'Custom attribute'
    : target === 'drop'
      ? 'Not imported'
      : ((ROLE_LABELS as Record<string, string>)[target] ?? FIELD_LABELS[target as SheetMetadataField])

const CHIP_ORDER: ColumnTarget[] = [...ROLE_ORDER, ...METADATA_FIELD_OPTIONS, 'custom', 'drop']

const chipStyle = (target: ColumnTarget) =>
  (SHEET_ROLES as readonly string[]).includes(target)
    ? 'badge-outline'
    : target === 'drop'
      ? 'badge-ghost text-slate-400 line-through'
      : 'badge-ghost'

const sameTargets = (a: Record<string, ColumnTarget>, b: Record<string, ColumnTarget>) =>
  Object.keys(a).every((column) => a[column] === b[column])

const example = (sheet: SheetState, column: string) => sheet.rows.find((row) => row[column]?.trim())?.[column]?.trim()

/**
 * Column targets, applied together as a new sheet revision, so every item gets its row written again.
 * A field taken from another column leaves that column not imported.
 */
const MappingEditor = ({ draft, sheet, focus, onClose }: { draft: DraftState; sheet: SheetState; focus?: string; onClose: () => void }) => {
  const dispatch = useDispatch()
  const current = columnTargets(sheet)
  const [targets, setTargets] = useState(current)
  const unchanged = sameTargets(targets, current)
  const apply = () => {
    dispatch(wizard.sheetMappingChanged({ draftId: draft.id, targets }))
    onClose()
  }
  return (
    <div className="mt-2 space-y-2" data-testid="wizard-sheet-mapping-editor" onKeyDown={(event) => event.key === 'Escape' && onClose()}>
      <table className="table table-xs w-auto">
        <tbody>
          {sheet.headers.map((column) => {
            const sample = example(sheet, column)
            return (
              <tr key={column}>
                <th className="font-medium text-slate-800">{column}</th>
                <td>
                  <select
                    autoFocus={column === focus}
                    className="select select-bordered select-xs"
                    aria-label={`Column ${column}`}
                    value={targets[column]}
                    onChange={(event) => setTargets(retarget(targets, column, event.target.value as ColumnTarget))}
                  >
                    <optgroup label="Sample">
                      {ROLE_ORDER.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Metadata">
                      {METADATA_FIELD_OPTIONS.map((field) => (
                        <option key={field} value={field}>
                          {FIELD_LABELS[field]}
                        </option>
                      ))}
                    </optgroup>
                    <option value="custom">Custom attribute</option>
                    <option value="drop">Do not import</option>
                  </select>
                </td>
                <td className="max-w-48 truncate text-slate-500" title={sample}>
                  {sample && `e.g. ${sample}`}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={unchanged}
          data-testid="wizard-sheet-mapping-apply"
          onClick={apply}
        >
          Apply mapping
        </button>
        <button type="button" className="btn btn-ghost btn-sm" data-testid="wizard-sheet-mapping-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export const SheetPanel = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const [editing, setEditing] = useState<{ focus?: string } | undefined>()
  const sheet = draft.sheet
  if (!sheet) {
    return draft.sheetRef ? (
      <Callout
        as="section"
        id={SHEET_READD_TARGET}
        className="rounded-lg bg-white p-3 text-sm text-slate-700 transition-shadow"
        data-testid="wizard-sheet-readd"
      >
        Drop the sample sheet <span className="font-medium">{draft.sheetRef.name}</span> again: it was too large to keep in the browser.
        Your column choices come back with it.
      </Callout>
    ) : null
  }
  const targets = columnTargets(sheet)
  const chips = sheet.headers
    .map((column) => ({ column, target: targets[column] }))
    .sort((a, b) => CHIP_ORDER.indexOf(a.target) - CHIP_ORDER.indexOf(b.target))
  const hasItems = Object.keys(draft.groups).length > 0
  const { matched, written } = sheetRowCounts(draft)
  const coordinates = describeUnreadableCoordinates(sheet, controlSheetRows(draft))
  const swapHint = coordinateSwapHint(sheet)
  return (
    <section className="rounded-lg border border-slate-200 p-3" data-testid="wizard-sheet">
      <SectionHeading className="mb-1">Sample sheet: {sheet.name}</SectionHeading>
      <p className="text-sm text-slate-700">
        {sheet.rows.length} rows
        {hasItems && (
          <>
            {' '}
            · {matched} matched to {isArchiveOrder(draft) ? 'archives' : 'samples'} · {written} written into the order
          </>
        )}
      </p>
      {coordinates && (
        <p className="mt-1 text-sm text-amber-700" data-testid="wizard-sheet-coordinates">
          {coordinates}
        </p>
      )}
      {swapHint && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-amber-700" data-testid="wizard-sheet-coordinate-swap">
          <span>{describeCoordinateSwap(swapHint)}</span>
          <button
            type="button"
            className="btn btn-xs"
            onClick={() => dispatch(wizard.sheetMappingChanged({ draftId: draft.id, targets: swappedCoordinateTargets(sheet, swapHint) }))}
          >
            Swap Lat/Lon
          </button>
        </div>
      )}
      {editing ? (
        <MappingEditor draft={draft} sheet={sheet} focus={editing.focus} onClose={() => setEditing(undefined)} />
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {chips.map(({ column, target }) => (
            <button
              key={column}
              type="button"
              className={`badge badge-sm cursor-pointer hover:border-slate-500 ${chipStyle(target)}`}
              title={sheet.mapping.reasons[column] ?? 'Change what this column feeds'}
              data-testid="wizard-sheet-chip"
              data-column={column}
              onClick={() => setEditing({ focus: column })}
            >
              {targetLabel(target)}: {column}
            </button>
          ))}
          <button
            type="button"
            className="ml-1 text-xs text-slate-500 hover:text-slate-800 hover:underline"
            data-testid="wizard-sheet-edit-mapping"
            onClick={() => setEditing({})}
          >
            Edit mapping
          </button>
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-red-700 hover:underline"
            title="Take the sheet out: what it wrote into the order is cleared"
            data-testid="wizard-sheet-remove"
            onClick={() => dispatch(wizard.sheetRemoved({ draftId: draft.id }))}
          >
            Remove sheet
          </button>
        </div>
      )}
      {!sheet.mapping.sampleName && !isArchiveOrder(draft) && (
        <p className="mt-2 text-sm text-slate-700" data-testid="wizard-sheet-no-customer-names">
          No column gives customer sample names — samples are named after their files.{' '}
          {!editing && (
            <button
              type="button"
              className="text-slate-500 hover:text-slate-800 hover:underline"
              data-testid="wizard-sheet-pick-customer-names"
              onClick={() => setEditing({})}
            >
              Pick the column in the mapping
            </button>
          )}
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500" data-testid="wizard-sheet-values-hint">
        To change values, fix the sheet and drop it again, or use Edit Data in the classic view.
      </p>
      {sheet.notes.map((note) => (
        <p key={note} className="mt-1 text-xs text-slate-500">
          {note}
        </p>
      ))}
    </section>
  )
}
